import { GET, POST } from '../api';

export interface RatingValue {
  score: number;
  count: number;
  percentage: number;
}

export interface NpsSummary {
  promotersCount: number;
  passivesCount: number;
  detractorsCount: number;
  promotersPercentage: number;
  passivesPercentage: number;
  detractorsPercentage: number;
  npsScore: number;
}

export interface QuestionAnalytics {
  order: number;
  label: string;
  type: string;
  metrics: {
    totalResponses: number;
    averageScore?: number;
    distribution?: Record<string, number>;
    topResponses?: string[];
  };
}

export interface FormAnalytics {
  formId: number;
  formTitle: string;
  totalReviews: number;
  averageRating: number;
  npsSummary: NpsSummary;
  ratingDistribution: RatingValue[];
  questions: QuestionAnalytics[];
  anonymousCount: number;
  namedCount: number;
  dateRange: {
    from: string;
    to: string;
  };
}

export interface FeedbackAnalyticsDto {
  pluginspaceId: number;
  totalReviews: number;
  averageRating: number;
  npsSummary: NpsSummary;
  ratingDistribution: RatingValue[];
  topRatedForms: FormAnalytics[];
  lowestRatedForms: FormAnalytics[];
  allForms: FormAnalytics[];
  trendData: {
    date: string;
    averageRating: number;
    npsScore: number;
    totalReviews: number;
  }[];
  dateRange: {
    from: string;
    to: string;
  };
}

export interface AnalyticsFilter {
  dateFrom?: string;
  dateTo?: string;
  feedBackFormId?: number;
}

export interface FeedbackFormOption {
  id: number;
  title: string;
}

class FeedbackAnalyticsService {
  private buildQueryString(filter: AnalyticsFilter): string {
    const params = new URLSearchParams();

    if (filter.dateFrom) {
      params.append('dateFrom', filter.dateFrom);
    }

    if (filter.dateTo) {
      params.append('dateTo', filter.dateTo);
    }

    if (filter.feedBackFormId !== undefined) {
      params.append('feedBackFormId', String(filter.feedBackFormId));
    }

    const query = params.toString();
    return query ? `?${query}` : '';
  }

  async getAnalytics(filter: AnalyticsFilter): Promise<FeedbackAnalyticsDto> {
    try {
      const queryString = this.buildQueryString(filter);
      const response = await GET(
        `/feedback-review-hotel/analytics/dashboard${queryString}`,
      );

      if (response?.success) {
        return response.data;
      }

      throw new Error(response?.message || 'Erro ao carregar analytics');
    } catch (error: any) {
      console.error('Erro ao buscar analytics:', error);
      throw error;
    }
  }

  async getFeedbackForms(): Promise<FeedbackFormOption[]> {
    try {
      const response = await POST('/feedbackform/filter', {});
      const rawForms = Array.isArray(response)
        ? response
        : response?.dados || response?.data || response?.items || [];

      if (!Array.isArray(rawForms)) {
        return [];
      }

      return rawForms
        .filter((item: any) => item && item.id && item.title)
        .map((item: any) => ({
          id: Number(item.id),
          title: String(item.title),
        }));
    } catch (error) {
      console.error('Erro ao buscar formulários de feedback:', error);
      return [];
    }
  }
}

const feedbackAnalyticsService = new FeedbackAnalyticsService();

export default feedbackAnalyticsService;
